import './index.css'
import * as React from 'react'
import validation from 'react-validation-mixin'
import strategy from 'react-validatorjs-strategy'
import scrollIntoView from 'scroll-into-view-if-needed'
import type { Campaign, CampaignSection } from 'types/content-editor'
import type { ValidationProps } from 'types/validation'
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_LINE_HEIGHT,
  FONT_FAMILY_OPTIONS,
  lineHeightOptions,
} from '@disco/constants/content-editor'
import { getMaxImageWidth } from '@disco/content-editor/editor-utils'
import { updateBlock, updateColumn } from '@disco/content-editor/section'
import Button from 'components/ui/button'
import ColorInput from 'components/ui/color-input'
import Dropdown from 'components/ui/dropdowns/native-dropdown'
import Heading from 'components/ui/heading'
import Input from 'components/ui/input'
import Label from 'components/ui/label'
import ButtonBlockSettings from '../block-settings/button'
import FooterBlockSettings from '../block-settings/footer'
import ImageBlockSettings from '../block-settings/image'
import PlaylistBlockSettings from '../block-settings/playlist'
import { getValidatorData, getValidatorTypes } from './section-validation'
const emptySectionTextColorSettings = ['image', 'button', 'divider']
const emptyBlockSettings = [
  'image-and-text',
  'text',
  'two-column-text',
  'divider',
]
const emptyFontSizeSettings = ['divider', 'image']
const emptyLineHeightSettings = [
  'divider',
  'image',
  'button',
  'playlist',
  'footer',
]

const isSettingVisible = (section: CampaignSection, emptyTypes: string[]) =>
  !!section.columns.find(({ columnContent }) => {
    return (
      columnContent &&
      columnContent.find(({ type }) => !emptyTypes.includes(type))
    )
  })

type Props = {
  campaign: Campaign
  section: CampaignSection
  sectionIndex: number
  updateSection: (...args: Array<any>) => any
  updateCampaignFooter: (...args: Array<any>) => any
  createCampaignFooter: (...args: Array<any>) => any
}

class SectionSettings extends React.Component<
  Props & ValidationProps,
  {
    disableApply: boolean
    tabIndex: number
  }
> {
  _sectionRef: HTMLDivElement | null | undefined
  validatorTypes = getValidatorTypes(this.props.section)
  getValidatorData = () => getValidatorData(this.props.section)
  state = {
    disableApply: false,
    tabIndex: 0,
  }
  _handleTabChange = (tabIndex: number) =>
    this.setState({
      tabIndex,
    })

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (this.props.sectionIndex !== nextProps.sectionIndex) {
      const blockSettingsVisible = isSettingVisible(
        nextProps.section,
        emptyBlockSettings,
      )
      const tabIndex = !blockSettingsVisible ? 0 : this.state.tabIndex
      this.setState({
        tabIndex,
      })
    }
  }

  _onSectionPropChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      [e.target.name]: e.target.value,
    })
  }
  _onFontFamilySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      fontFamily: e.target.value,
    })
  }
  _onLineHeightSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      lineHeight: parseFloat(e.target.value),
    })
  }
  _onSectionColorPropChange = (prop: string, color: Record<string, any>) => {
    this.props.updateSection(this.props.sectionIndex, {
      ...this.props.section,
      [prop]: color.hex,
    })
  }
  _onSubmit = () => {
    this.props.validate(error => {
      if (!error) {
        this._onApply()
      } else {
        if (!this._sectionRef) return

        const errorItems =
          this._sectionRef.getElementsByClassName('label__error')

        if (errorItems.length > 0) {
          const focusedItem = errorItems[0]
          scrollIntoView(focusedItem, {
            behavior: 'smooth',
            scrollMode: 'if-needed',
            boundary: this._sectionRef,
          })
        }
      }
    })
  }
  _onApply = async () => {
    let footerUpdate = null
    let updatedSection = {
      ...this.props.section,
      columns: this.props.section.columns.map((column, columnIndex) => {
        return {
          ...column,
          columnContent: (column.columnContent || []).map(
            (content, contentIndex) => {
              const sourceColumn = this.props.section.columns[columnIndex]

              // for text content we should merge source content edited inline
              // with the rest of section/content props to not to loose data
              if (content.type === 'text' && sourceColumn.columnContent) {
                return sourceColumn.columnContent[contentIndex]
              } else if (content.type === 'footer') {
                const { footer, ...rest } = content
                footerUpdate = footer
                  ? { ...footer, social_links: footer.social_links }
                  : null
                return rest
              }

              return content
            },
          ),
        }
      }),
    }

    if (footerUpdate) {
      try {
        let resultId = footerUpdate.id

        if (footerUpdate.id) {
          //update
          await this.props.updateCampaignFooter(footerUpdate.id, footerUpdate)
        } else {
          // create
          const result = await this.props.createCampaignFooter(footerUpdate)
          resultId = result.id
        }

        if (
          updatedSection.columns.length > 0 &&
          updatedSection.columns[0].columnContent &&
          updatedSection.columns[0].columnContent.length > 0 &&
          updatedSection.columns[0].columnContent[0].type === 'footer'
        ) {
          const footerContent = updatedSection.columns[0]
            .columnContent[0] as any
          footerContent.footer_id = resultId
        }
      } catch (error) {}
    }

    this.props.updateSection(this.props.sectionIndex, {
      ...updatedSection,
      editMode: false,
    })
  }
  _updateBlock = (columnIndex: number, contentIndex: number, update: any) => {
    const { section } = this.props
    const updatedColumn = updateBlock(
      section.columns[columnIndex],
      contentIndex,
      update,
    )
    const updatedSection = updateColumn(section, columnIndex, updatedColumn)
    this.props.updateSection(this.props.sectionIndex, updatedSection)
  }
  _disableApply = () =>
    this.setState({
      disableApply: true,
    })
  getValidationMessage = (
    field: string,
    columnIndex: number,
    contentIndex: number,
  ) =>
    this.props
      .getValidationMessages(`${columnIndex}.${contentIndex}.${field}`)
      .toString()
      .replace(`${columnIndex}.${contentIndex}.`, '')
  getValidationErrors = (
    fields: string[],
    columnIndex: number,
    contentIndex: number,
  ) => {
    return fields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: this.getValidationMessage(field, columnIndex, contentIndex),
      }),
      {},
    )
  }

  render() {
    const { section, campaign, sectionIndex } = this.props
    const blockSettingsVisible = isSettingVisible(section, emptyBlockSettings)
    const sectionTextColorVisible = isSettingVisible(
      section,
      emptySectionTextColorSettings,
    )
    const fontSizeSettingsVisible = isSettingVisible(
      section,
      emptyFontSizeSettings,
    )
    const fontFamilySettingsVisible = isSettingVisible(
      section,
      emptyFontSizeSettings,
    )
    const lineHeightSettingsVisible = isSettingVisible(
      section,
      emptyLineHeightSettings,
    )
    return (
      <div
        className="content-editor__section-settings"
        ref={r => {
          this._sectionRef = r
        }}
      >
        <div className="content-editor__sidebar__header">
          <Heading size={500}>Block Settings</Heading>
        </div>
        <div className="content-editor__section-settings__body">
          <div className="content-editor__section-settings__list">
            <div className="rich-text-toolbar__settings__list__row">
              <Label caption="Background color" />
              <div className="content-editor__section-settings__list__value">
                <ColorInput
                  name="backgroundColor"
                  value={section.backgroundColor || ''}
                  portal
                  onChange={color =>
                    this._onSectionColorPropChange('backgroundColor', color)
                  }
                />
              </div>
            </div>
            {sectionTextColorVisible && (
              <div className="rich-text-toolbar__settings__list__row">
                <Label caption="Text color" />
                <div className="content-editor__section-settings__list__value">
                  <ColorInput
                    name="color"
                    value={section.color || ''}
                    portal
                    onChange={color =>
                      this._onSectionColorPropChange('color', color)
                    }
                  />
                </div>
              </div>
            )}
            {fontFamilySettingsVisible && (
              <div className="rich-text-toolbar__settings__list__row">
                <Label caption="Font family" />
                <div className="content-editor__section-settings__list__value">
                  <Dropdown
                    className="SelectDropdown toolbar-dropdown"
                    options={FONT_FAMILY_OPTIONS}
                    value={
                      section.fontFamily ||
                      campaign.fontFamily ||
                      DEFAULT_FONT_FAMILY
                    }
                    onChange={this._onFontFamilySelect}
                  />
                </div>
              </div>
            )}
            {fontSizeSettingsVisible && (
              <div className="rich-text-toolbar__settings__list__row">
                <Label caption="Font size (px)" />
                <div className="content-editor__section-settings__list__value">
                  <Input
                    className="Campaigns_LargeInput"
                    name="fontSize"
                    value={section.fontSize || ''}
                    onChange={this._onSectionPropChange}
                  />
                </div>
              </div>
            )}
            {lineHeightSettingsVisible && (
              <div className="rich-text-toolbar__settings__list__row">
                <Label caption="Line height" />
                <div className="content-editor__section-settings__list__value">
                  <Dropdown
                    className="SelectDropdown toolbar-dropdown"
                    options={lineHeightOptions}
                    value={(
                      section.lineHeight ||
                      campaign.lineHeight ||
                      DEFAULT_LINE_HEIGHT
                    ).toString()}
                    onChange={this._onLineHeightSelect}
                  />
                </div>
              </div>
            )}
            <div className="rich-text-toolbar__settings__list__row">
              <Label caption="Vertical padding (px)" />
              <div className="content-editor__section-settings__list__value">
                <Input
                  className="Campaigns_LargeInput"
                  name="verticalPadding"
                  value={section.verticalPadding || ''}
                  onChange={this._onSectionPropChange}
                />
              </div>
            </div>
            <div className="rich-text-toolbar__settings__list__row">
              <Label caption="Horizontal padding (px)" />
              <div className="content-editor__section-settings__list__value">
                <Input
                  className="Campaigns_LargeInput"
                  name="horizontalPadding"
                  value={section.horizontalPadding || ''}
                  onChange={this._onSectionPropChange}
                />
              </div>
            </div>
          </div>
          {blockSettingsVisible && (
            <div className="content-editor__section-settings__list content-editor__section-settings__list--block">
              {section.columns.map(({ columnContent }, columnIndex) => {
                if (!columnContent) return null
                return columnContent.map((content, contentIndex) => {
                  let child = null

                  const _genericUpdate = update => {
                    if (update.src && update.width) {
                      this.setState({
                        disableApply: false,
                      })
                    }

                    this._updateBlock(columnIndex, contentIndex, {
                      ...content,
                      ...update,
                    })
                  }

                  if (content.type === 'playlist') {
                    child = (
                      <PlaylistBlockSettings
                        content={content}
                        onChange={update =>
                          this._updateBlock(columnIndex, contentIndex, update)
                        }
                      />
                    )
                  } else if (content.type === 'footer') {
                    child = (
                      <FooterBlockSettings
                        content={content}
                        onChange={_genericUpdate}
                        validationErrors={this.getValidationErrors(
                          [
                            'apple',
                            'facebook',
                            'instagram',
                            'soundcloud',
                            'spotify',
                            'twitter',
                            'web',
                            'youtube',
                          ],
                          columnIndex,
                          contentIndex,
                        )}
                      />
                    )
                  } else if (content.type === 'image') {
                    const {
                      sectionIndex: si,
                      columnIndex: ci,
                      contentIndex: coi,
                      editMode,
                      type,
                      ...blockSettings
                    } = content
                    const maxImageWidth = getMaxImageWidth(section, campaign)
                    child = (
                      <ImageBlockSettings
                        content={blockSettings}
                        onChange={_genericUpdate}
                        maxImageWidth={maxImageWidth}
                        onImageSet={_genericUpdate}
                        onImageUpload={this._disableApply}
                        validationErrors={{
                          src: this.getValidationMessage(
                            'location',
                            columnIndex,
                            contentIndex,
                          ),
                          link: this.getValidationMessage(
                            'link',
                            columnIndex,
                            contentIndex,
                          ),
                        }}
                      />
                    )
                  } else if (content.type === 'button') {
                    const {
                      sectionIndex: si,
                      columnIndex: ci,
                      contentIndex: coi,
                      editMode,
                      type,
                      ...blockSettings
                    } = content
                    child = (
                      <ButtonBlockSettings
                        content={blockSettings}
                        onChange={_genericUpdate}
                        validationErrors={{
                          link: this.getValidationMessage(
                            'link',
                            columnIndex,
                            contentIndex,
                          ),
                        }}
                      />
                    )
                  }

                  return (
                    <div
                      key={`element-${sectionIndex}-${columnIndex}-${contentIndex}`}
                    >
                      {!!child && section.columns.length > 1 && (
                        <div className="content-editor__section-settings__list__column-header">
                          Column #{columnIndex + 1}
                        </div>
                      )}
                      {child}
                    </div>
                  )
                })
              })}
            </div>
          )}
        </div>
        <div className="content-editor__section-settings__buttons">
          <Button
            appearance="primary"
            caption="Save and Close"
            disabled={this.state.disableApply}
            onClick={this._onSubmit}
          />
        </div>
      </div>
    )
  }
}

export default validation(strategy)(SectionSettings) as any
